import {
  ISettingsSearchContext,
  ISettingsSearchEntry,
  ISettingsTab
} from '@configuration/shared/settings-search/settings-search.interfaces';
import { NOTIFICATION_MATRIX_ROWS } from '@configuration/shared/components/notification-matrix/notification-matrix.rows';
import { ISetting } from '@shared/interfaces/setting.interface';

/**
 * Single source of truth for the settings panel: the tabs (with the same
 * gating the shell uses to disable them) and every setting card a user can
 * land on, with the i18n keys the tab template renders it with and the
 * condition under which the template renders it.
 *
 * Every entry's `id` must match an `appSettingAnchor` in the tab template.
 */

type Rule = (context: ISettingsSearchContext) => boolean;

/** Mirrors the tabs' `getSettingValue`: `settings_json` wins over the column of the same name. */
export function settingValue(settings: ISetting | undefined, key: string): any {
  const json = settings?.settings_json as any;

  return json && json[key] !== undefined ? json[key] : (settings as any)?.[key];
}

const isOn =
  (key: string): Rule =>
  ({ settings }) =>
    !!settingValue(settings, key);
const isOff =
  (key: string): Rule =>
  (context) =>
    !isOn(key)(context);
const all =
  (...rules: Rule[]): Rule =>
  (context) =>
    rules.every((rule: Rule) => rule(context));
const never: Rule = () => false;

const isDemo: Rule = ({ settings }) => !!settings?.is_demo_enabled;
const hasCandidateFeedback: Rule = ({ settings }) =>
  !!settings?.is_plugins_candidate_feedback_enabled;
const hasAnyFeedback: Rule = ({ settings }) =>
  !!settings?.is_plugins_candidate_feedback_enabled ||
  !!settings?.is_plugins_recruiter_feedback_enabled;
const hasPreScreening: Rule = ({ settings }) =>
  !!settings?.is_plugins_pre_screening_enabled;
const hasCandidatePendingNotification: Rule = ({ settings }) =>
  !!settings?.is_notify_candidate_reference_pending_enabled;
const hasMultiLanguages: Rule = ({ settings }) =>
  !!settings?.is_plugins_multi_languages_enabled;
const hasWhiteLabel: Rule = ({ settings }) =>
  !!settings?.is_plugins_white_label_enabled;
const hasDataRetentionPlugin: Rule = ({ settings }) =>
  !!settings?.plugins_json?.data_retention_plugin;
const hasTextMessages: Rule = ({ settings }) =>
  !!settings?.is_text_messages_enabled;
const hasNewLeadSystem: Rule = ({ settings }) =>
  !!(
    settings?.settings_json?.lead_generation_setting?.enabled ||
    settings?.settings_json?.lead_generation?.enabled
  );
const hasAutoReminders: Rule = ({ settings }) =>
  !!settings?.settings_json?.auto_reminders?.is_enabled;
const hasUsageNotifications: Rule = ({ settings }) =>
  !!settings?.settings_json?.usage_notifications?.is_enabled;
const hasReportRecipients: Rule = ({ settings }) =>
  !!settings?.settings_json?.candidate_report_recipients;
const hasReportRecipientsEnabled: Rule = ({ settings }) =>
  !!settings?.settings_json?.candidate_report_recipients?.is_enabled;
const hasCandidateDocuments: Rule = ({ settings }) =>
  !!settings?.settings_json?.candidate_documents?.enabled;
const hasDataRetention: Rule = ({ settings }) =>
  !!settings?.settings_json?.data_retention?.is_enabled;
const isParentAccount: Rule = ({ hiringFirm }) =>
  hiringFirm?.is_standalone === false;

export const SETTINGS_TABS: ISettingsTab[] = [
  { label: 'TABS.GENERAL', url: 'general', isDisabled: isDemo },
  { label: 'TABS.CONFIGURATION', url: 'configuration', isDisabled: isDemo },
  { label: 'TABS.PLUGINS', url: 'plugins', isDisabled: isDemo },
  { label: 'TABS.RELATIONSHIPS', url: 'relationships', isDisabled: isDemo },
  { label: 'TABS.REQUESTS', url: 'requests', isDisabled: isDemo },
  { label: 'TABS.REFERENCES', url: 'references', isDisabled: isDemo },
  { label: 'TABS.JOBS', url: 'jobs', isDisabled: never },
  {
    // Admin-granted plugin: hidden until Vitay enables candidate feedback
    // for the firm, same as every other admin-granted plugin tab.
    label: 'TABS.FEEDBACK',
    url: 'feedbacks/employers',
    isDisabled: isDemo,
    isHidden: (context) => !hasCandidateFeedback(context)
  },
  { label: 'TABS.DOCUMENTS', url: 'documents', isDisabled: isDemo },
  { label: 'TABS.NOTIFICATIONS', url: 'notifications', isDisabled: isDemo },
  { label: 'TABS.LEADS', url: 'leads', isDisabled: isDemo },
  { label: 'TABS.PAGES', url: 'pages', isDisabled: isDemo },
  { label: 'TABS.EMAILS', url: 'emails', isDisabled: isDemo },
  { label: 'TABS.TEXTS', url: 'texts', isDisabled: isDemo },
  { label: 'TABS.DEVELOPERS', url: 'developers', isDisabled: isDemo },
  {
    // Admin-granted plugin: bulk anonymization stays hidden until Vitay
    // enables it for the firm
    label: 'TABS.DATA_RETENTION',
    url: 'data-retention',
    isDisabled: isDemo,
    isHidden: (context) => !hasDataRetentionPlugin(context)
  },
  {
    // Admin-granted plugin: hidden until Vitay enables white labeling for
    // the firm. Not demo-disabled: white-labeled demo firms still need it.
    label: 'TABS.WHITE_LABEL',
    url: 'white-label',
    isDisabled: never,
    isHidden: (context) => !hasWhiteLabel(context)
  },
  {
    // Provisioning, not is_stytch_sso_enabled: this tab hosts SCIM and
    // Member Management alongside SSO, and a firm can run Stytch SCIM
    // while authenticating through a different path (ruby-saml).
    label: 'TABS.SSO',
    url: 'sso',
    isDisabled: never,
    isHidden: ({ hiringFirm }) => !hiringFirm?.stytch_organization_id
  }
];

type EntryOptions = Pick<ISettingsSearchEntry, 'keywords' | 'isVisible'>;

const entry = (
  tab: string,
  id: string,
  titleKey: string,
  descriptionKey?: string,
  options: EntryOptions = {}
): ISettingsSearchEntry => ({ id, tab, titleKey, descriptionKey, ...options });

/** One entry per key rendered by a `@for (p of params)` loop: `<namespace>.<key>.TITLE` / `.DESCRIPTION`. */
const keyed = (
  tab: string,
  namespace: string,
  keys: string[],
  options: EntryOptions = {}
): ISettingsSearchEntry[] =>
  keys.map((key: string) =>
    entry(
      tab,
      key,
      `${namespace}.${key}.TITLE`,
      `${namespace}.${key}.DESCRIPTION`,
      options
    )
  );

/** Email / text templates: one card per category, plus a `_reminder` card for categories that have reminders. */
const templates = (
  tab: string,
  namespace: string,
  categories: { category: string; hasReminder?: boolean; isVisible?: Rule }[],
  keywords: string[],
  baseRule?: Rule
): ISettingsSearchEntry[] =>
  categories.flatMap(({ category, hasReminder, isVisible }) => {
    const rules = [baseRule, isVisible].filter((rule): rule is Rule => !!rule);
    const options: EntryOptions = {
      keywords,
      isVisible: rules.length ? all(...rules) : undefined
    };
    const variants = [
      entry(
        tab,
        category,
        `${namespace}.${category}.DIRECT.TITLE`,
        `${namespace}.${category}.DIRECT.DESCRIPTION`,
        options
      )
    ];

    if (hasReminder) {
      variants.push(
        entry(
          tab,
          `${category}_reminder`,
          `${namespace}.${category}.REMINDER.TITLE`,
          `${namespace}.${category}.REMINDER.DESCRIPTION`,
          { ...options, keywords: [...keywords, 'reminder'] }
        )
      );
    }

    return variants;
  });

const CONFIGURATION = 'SETTINGS.CONFIGURATION';
const REQUESTS = 'SETTINGS.REQUESTS';
const REFERENCES = 'SETTINGS.REFERENCES';
const DOCUMENTS = 'SETTINGS.DOCUMENTS';
const NOTIFICATIONS = 'SETTINGS.CONFIGURATION';
const WHITE_LABEL = 'SETTINGS.WHITE_LABEL';

export const SETTINGS_SEARCH_ENTRIES: ISettingsSearchEntry[] = [
  // General
  entry(
    'general',
    'company_name',
    'SETTINGS.GENERAL.COMPANY_NAME.TITLE',
    'SETTINGS.GENERAL.COMPANY_NAME.DESCRIPTION'
  ),
  entry(
    'general',
    'brand_name',
    'SETTINGS.GENERAL.BRAND_NAME.TITLE',
    'SETTINGS.GENERAL.BRAND_NAME.DESCRIPTION',
    { keywords: ['brand', 'candidate', 'reference', 'nickname'] }
  ),
  entry(
    'general',
    'logo',
    'SETTINGS.GENERAL.LOGO.TITLE',
    'SETTINGS.GENERAL.LOGO.DESCRIPTION',
    { keywords: ['brand', 'image'] }
  ),
  entry(
    'general',
    'country',
    'SETTINGS.GENERAL.COUNTRY.TITLE',
    'SETTINGS.GENERAL.COUNTRY.DESCRIPTION'
  ),
  entry(
    'general',
    'timezone',
    'SETTINGS.GENERAL.TIMEZONE.TITLE',
    'SETTINGS.GENERAL.TIMEZONE.DESCRIPTION'
  ),
  entry(
    'general',
    'date_format',
    'SETTINGS.GENERAL.DATE_FORMAT.TITLE',
    'SETTINGS.GENERAL.DATE_FORMAT.DESCRIPTION'
  ),
  entry(
    'general',
    'language',
    'SETTINGS.GENERAL.LANGUAGE.TITLE',
    'SETTINGS.GENERAL.LANGUAGE.DESCRIPTION',
    {
      keywords: ['locale', 'translation']
    }
  ),
  entry(
    'general',
    'colors_primary',
    'SETTINGS.GENERAL.COLORS_PRIMARY.TITLE',
    'SETTINGS.GENERAL.COLORS_PRIMARY.DESCRIPTION',
    {
      keywords: ['brand', 'theme']
    }
  ),
  entry(
    'general',
    'colors_secondary',
    'SETTINGS.GENERAL.COLORS_SECONDARY.TITLE',
    'SETTINGS.GENERAL.COLORS_SECONDARY.DESCRIPTION',
    {
      keywords: ['brand', 'theme']
    }
  ),

  // Configuration
  ...keyed('configuration', CONFIGURATION, ['is_banner_enabled']),
  entry(
    'configuration',
    'banner_text',
    `${CONFIGURATION}.is_banner_enabled.TEXT.TITLE`,
    `${CONFIGURATION}.is_banner_enabled.TEXT.DESCRIPTION`
  ),
  ...keyed('configuration', CONFIGURATION, [
    'default_filters_timeframe',
    'rating_scale',
    'is_force_users_password_update_enabled',
    'password_expiry_days',
    'is_metadata_collection_enabled',
    'is_status_change_comment_prompt_enabled'
  ]),
  ...keyed('configuration', CONFIGURATION, ['is_disable_inactive_users_enabled'], {
    keywords: ['disable', 'inactive', 'lock', 'locked', 'sign in']
  }),
  ...keyed('configuration', CONFIGURATION, ['is_all_toogle_enabled'], {
    keywords: ['toggle', 'everyone', 'myself']
  }),
  entry(
    'configuration',
    'is_branding_emails_from_enabled',
    `${CONFIGURATION}.EMAIL_SENDER.TITLE`,
    `${CONFIGURATION}.EMAIL_SENDER.DESCRIPTION`,
    { keywords: ['branding', 'from address', 'sender name'] }
  ),
  entry(
    'configuration',
    'branding_email_from',
    `${CONFIGURATION}.EMAIL_SENDER.FROM_ADDRESS`,
    `${CONFIGURATION}.EMAIL_SENDER.FROM_ADDRESS_HELP`,
    { keywords: ['sender', 'branding'] }
  ),
  entry(
    'configuration',
    'branding_email_name',
    `${CONFIGURATION}.EMAIL_SENDER.FROM_NAME`,
    `${CONFIGURATION}.EMAIL_SENDER.FROM_NAME_HELP`,
    { keywords: ['sender', 'branding'] }
  ),
  entry(
    'configuration',
    'is_reply_to_custom_enabled',
    `${CONFIGURATION}.EMAIL_SENDER.REPLY_TO`,
    `${CONFIGURATION}.EMAIL_SENDER.REPLY_TO_HELP`,
    { keywords: ['reply-to', 'reply', 'assigned recruiter'] }
  ),
  entry(
    'configuration',
    'reply_to_email',
    `${CONFIGURATION}.EMAIL_SENDER.REPLY_TO_ADDRESS`,
    undefined,
    {
      keywords: ['reply-to', 'reply'],
      isVisible: isOn('is_reply_to_custom_enabled')
    }
  ),
  entry(
    'configuration',
    'reply_to_name',
    `${CONFIGURATION}.EMAIL_SENDER.REPLY_TO_NAME`,
    undefined,
    {
      keywords: ['reply-to', 'reply'],
      isVisible: isOn('is_reply_to_custom_enabled')
    }
  ),
  ...keyed('configuration', CONFIGURATION, [
    'is_candidate_email_required',
    'is_candidate_ownership_updatable',
    'is_phone_number_lookup_enabled'
  ]),
  ...keyed(
    'configuration',
    CONFIGURATION,
    ['is_idle_enabled', 'idle_timeout'],
    { keywords: ['inactivity', 'log out'] }
  ),
  ...keyed(
    'configuration',
    CONFIGURATION,
    ['new_recruiters_security_statement'],
    { keywords: ['compliance'] }
  ),

  // Plugins
  entry(
    'plugins',
    'multi_languages',
    'SETTINGS.PLUGINS.MULTI_LANGUAGES.TITLE',
    'SETTINGS.PLUGINS.MULTI_LANGUAGES.DESCRIPTION',
    {
      keywords: ['translation', 'locale'],
      isVisible: hasMultiLanguages
    }
  ),

  // Relationships
  entry(
    'relationships',
    'reference_relationships',
    'MODALS.REFERENCE_RELATIONSHIPS.TITLE',
    'MODALS.REFERENCE_RELATIONSHIPS.PAGE_DESCRIPTION',
    { keywords: ['relationship', 'manager', 'colleague'] }
  ),

  // Ref. Requests
  entry(
    'requests',
    'min_reference_count',
    `${REQUESTS}.MIN_REFERENCE_COUNT.TITLE`,
    `${REQUESTS}.MIN_REFERENCE_COUNT.DESCRIPTION`
  ),
  ...keyed('requests', REQUESTS, [
    'review_candidate_references',
    'is_candidate_authorization_enabled',
    'is_assigned_user_checkbox_enabled',
    'is_additional_references_enabled',
    'is_managers_only_default_enabled'
  ]),
  ...keyed('requests', REQUESTS, ['candidate_documents'], {
    keywords: ['resume', 'upload', 'attachment']
  }),
  entry(
    'requests',
    'candidate_documents_required',
    `${REQUESTS}.candidate_documents.REQUIRED`,
    undefined,
    {
      keywords: ['document', 'upload'],
      isVisible: hasCandidateDocuments
    }
  ),
  entry(
    'requests',
    'candidate_documents_text',
    `${REQUESTS}.candidate_documents.TEXT_LABEL`,
    undefined,
    {
      keywords: ['document', 'upload'],
      isVisible: hasCandidateDocuments
    }
  ),

  // References: the reference's own record and what the reference can do
  // when responding, as opposed to how the request reaches the candidate.
  ...keyed('references', REFERENCES, ['is_reference_email_required']),
  ...keyed('references', REFERENCES, ['is_work_phone_number_enabled'], {
    keywords: ['phone']
  }),
  ...keyed('references', REFERENCES, ['phone_requirement'], {
    keywords: ['phone', 'mobile', 'work'],
    isVisible: isOn('is_work_phone_number_enabled')
  }),
  ...keyed('references', REFERENCES, ['is_reference_mobile_number_required'], {
    keywords: ['phone'],
    isVisible: isOff('is_work_phone_number_enabled')
  }),
  ...keyed('references', REFERENCES, [
    'is_reference_address_enabled',
    'is_reference_address_required',
    'is_references_specialty_required',
    'is_reference_candidate_position',
    'is_reference_dates_enabled'
  ]),
  ...keyed('references', REFERENCES, ['is_reference_end_date_restricted'], {
    isVisible: isOn('is_reference_dates_enabled')
  }),
  entry(
    'references',
    'reference_end_date_max_age',
    `${REFERENCES}.reference_end_date_max_age.LABEL`,
    undefined,
    {
      keywords: ['end date', 'age'],
      isVisible: all(
        isOn('is_reference_dates_enabled'),
        isOn('is_reference_end_date_restricted')
      )
    }
  ),
  ...keyed('references', REFERENCES, ['is_personal_emails_allowed'], {
    keywords: ['gmail', 'domain']
  }),
  ...keyed('references', REFERENCES, [
    'is_prevent_duplicate_references_enabled',
    'reference_authorization',
    'is_references_consent_enabled'
  ]),
  entry(
    'references',
    'is_consent_enabled_to_client',
    `${REFERENCES}.is_references_consent_enabled.TO_CLIENT`,
    undefined,
    {
      keywords: ['consent'],
      isVisible: ({ settings }) =>
        settings?.is_references_consent_enabled === true
    }
  ),
  entry(
    'references',
    'is_consent_enabled_to_candidate',
    `${REFERENCES}.is_references_consent_enabled.TO_CANDIDATE`,
    undefined,
    {
      keywords: ['consent'],
      isVisible: ({ settings }) =>
        settings?.is_references_consent_enabled === true
    }
  ),
  ...keyed('references', REFERENCES, [
    'is_reference_is_declinable_enabled',
    'is_call_back_enabled',
    'is_reference_score_enabled'
  ]),

  // Jobs
  ...keyed('jobs', REQUESTS, ['is_candidate_job_title_enabled']),
  ...keyed('jobs', REQUESTS, ['is_job_title_required'], {
    isVisible: ({ settings }) => !!settings?.is_candidate_job_title_enabled
  }),
  entry('jobs', 'jobs_list', 'SETTINGS.JOBS.TITLE', 'SETTINGS.JOBS.NOTE', {
    keywords: ['question set', 'position']
  }),

  // Feedback (employers)
  ...keyed('feedbacks/employers', REQUESTS, [
    'is_employer_feedbacks_address_enabled',
    'is_employer_feedbacks_address_required',
    'is_employer_feedbacks_dates_enabled',
    'is_employer_feedbacks_candidate_position_enabled',
    'is_employer_feedbacks_specialty_enabled'
  ]),

  // Documents
  ...keyed('documents', DOCUMENTS, [
    'is_pdf_watermark_enabled',
    'is_show_reference_edits_on_report',
    'is_hide_report_data'
  ]),
  ...[
    'is_hide_report_name',
    'is_hide_report_phone',
    'is_hide_report_email',
    'is_hide_report_job',
    'is_hide_report_company',
    'is_hide_report_candidate_name',
    'is_hide_report_brand'
  ].map((key: string) =>
    // These rows only carry a description in the locale files; it is the label the tab renders.
    entry('documents', key, `${DOCUMENTS}.${key}.DESCRIPTION`, undefined, {
      keywords: ['hide', 'personal data', 'report']
    })
  ),

  // Notifications
  entry(
    'notifications',
    'team_notifications',
    `${NOTIFICATIONS}.TEAM_NOTIFICATIONS.TITLE`,
    `${NOTIFICATIONS}.TEAM_NOTIFICATIONS.DESCRIPTION`,
    { keywords: ['team', 'recipients', 'email', 'who', 'matrix'] }
  ),
  ...NOTIFICATION_MATRIX_ROWS.map((row) =>
    entry(
      'notifications',
      row.anchorId,
      `${NOTIFICATIONS}.TEAM_NOTIFICATIONS.ROWS.${row.key}.TITLE`,
      `${NOTIFICATIONS}.TEAM_NOTIFICATIONS.ROWS.${row.key}.DESCRIPTION`,
      {
        keywords: ['team', 'notification', 'email', row.type.replace(/_/g, ' ')],
        isVisible: ({ settings }) => row.isVisible(settings)
      }
    )
  ),
  ...keyed('notifications', NOTIFICATIONS, ['candidate_report_recipients'], {
    keywords: ['report', 'recipients'],
    isVisible: hasReportRecipients
  }),
  entry(
    'notifications',
    'candidate_report_recipients_domain_restricted',
    `${NOTIFICATIONS}.candidate_report_recipients.is_domain_restricted.TITLE`,
    `${NOTIFICATIONS}.candidate_report_recipients.is_domain_restricted.DESCRIPTION`,
    {
      keywords: ['report', 'recipients', 'domain'],
      isVisible: all(hasReportRecipients, hasReportRecipientsEnabled)
    }
  ),
  entry(
    'notifications',
    'email_options',
    `${NOTIFICATIONS}.EMAIL_OPTIONS.TITLE`,
    `${NOTIFICATIONS}.EMAIL_OPTIONS.DESCRIPTION`,
    { keywords: ['email', 'pdf', 'sender'] }
  ),
  ...keyed('notifications', NOTIFICATIONS, [
    'is_attach_pdf_report_recruiter_email_enabled'
  ]),
  ...keyed('notifications', NOTIFICATIONS, ['is_ip_conflict_banner_enabled'], {
    isVisible: ({ settings }) =>
      !!settings?.is_attach_pdf_report_recruiter_email_enabled
  }),
  ...keyed('notifications', NOTIFICATIONS, ['is_requested_recruiter_from_email']),
  entry(
    'notifications',
    'candidate_notifications',
    `${NOTIFICATIONS}.CANDIDATE_NOTIFICATIONS.TITLE`,
    `${NOTIFICATIONS}.CANDIDATE_NOTIFICATIONS.DESCRIPTION`,
    { keywords: ['candidate', 'notification', 'email'] }
  ),
  ...keyed('notifications', NOTIFICATIONS, [
    'is_notify_candidate_reference_added_enabled',
    'is_notify_candidate_reference_pending_enabled',
    'is_notify_candidate_reference_completed_enabled',
    'is_notify_candidate_reference_email_activities_enabled'
  ]),
  entry(
    'notifications',
    'auto_reminders',
    `${NOTIFICATIONS}.auto_reminders.is_enabled.TITLE`,
    `${NOTIFICATIONS}.auto_reminders.is_enabled.DESCRIPTION`,
    { keywords: ['reminder', 'automatic'] }
  ),
  entry(
    'notifications',
    'auto_reminders_rate_hours',
    `${NOTIFICATIONS}.auto_reminders.rate_hours.TITLE`,
    `${NOTIFICATIONS}.auto_reminders.rate_hours.DESCRIPTION`,
    { keywords: ['reminder', 'frequency'], isVisible: hasAutoReminders }
  ),
  entry(
    'notifications',
    'auto_reminders_skip_weekend_days',
    `${NOTIFICATIONS}.auto_reminders.skip_weekend_days.TITLE`,
    `${NOTIFICATIONS}.auto_reminders.skip_weekend_days.DESCRIPTION`,
    { keywords: ['reminder', 'weekend'], isVisible: hasAutoReminders }
  ),
  entry(
    'notifications',
    'auto_reminders_feedback_reminders',
    `${NOTIFICATIONS}.auto_reminders.is_feedback_reminders_enabled.TITLE`,
    `${NOTIFICATIONS}.auto_reminders.is_feedback_reminders_enabled.DESCRIPTION`,
    {
      keywords: ['reminder', 'feedback'],
      isVisible: all(hasAutoReminders, hasAnyFeedback)
    }
  ),
  entry(
    'notifications',
    'usage_notifications',
    `${NOTIFICATIONS}.usage_notifications.is_enabled.TITLE`,
    `${NOTIFICATIONS}.usage_notifications.is_enabled.DESCRIPTION`,
    { keywords: ['usage', 'credits', 'billing'] }
  ),
  entry(
    'notifications',
    'usage_notifications_thresholds',
    `${NOTIFICATIONS}.usage_notifications.thresholds.TITLE`,
    `${NOTIFICATIONS}.usage_notifications.thresholds.DESCRIPTION`,
    { keywords: ['usage', 'percentage'], isVisible: hasUsageNotifications }
  ),
  entry(
    'notifications',
    'usage_notifications_notify_root_users',
    `${NOTIFICATIONS}.usage_notifications.notify_root_users.TITLE`,
    `${NOTIFICATIONS}.usage_notifications.notify_root_users.DESCRIPTION`,
    { keywords: ['usage'], isVisible: hasUsageNotifications }
  ),
  entry(
    'notifications',
    'usage_notifications_custom_emails',
    `${NOTIFICATIONS}.usage_notifications.custom_emails.TITLE`,
    `${NOTIFICATIONS}.usage_notifications.custom_emails.DESCRIPTION`,
    { keywords: ['usage', 'recipients'], isVisible: hasUsageNotifications }
  ),

  // Leads
  entry(
    'leads',
    'lead_generation',
    'SETTINGS.PAGES.LEADS.TITLE',
    'SETTINGS.PAGES.LEADS.DESCRIPTION',
    {
      keywords: ['lead generation', 'lead gen']
    }
  ),
  entry(
    'leads',
    'lead_generation_description',
    'SETTINGS.PAGES.LEADS.DESCRIPTION_LABEL',
    undefined,
    {
      keywords: ['lead generation', 'lead gen'],
      isVisible: hasNewLeadSystem
    }
  ),
  entry(
    'leads',
    'lead_generation_consent',
    'SETTINGS.PAGES.LEADS.CONSENT_LABEL',
    'SETTINGS.PAGES.LEADS.CONSENT_DESCRIPTION',
    {
      keywords: ['lead generation', 'lead gen', 'gdpr'],
      isVisible: hasNewLeadSystem
    }
  ),
  entry(
    'leads',
    'lead_types',
    'SETTINGS.PAGES.LEADS.LEAD_TYPES_TITLE',
    'SETTINGS.PAGES.LEADS.MODE_LABEL',
    {
      keywords: ['lead generation', 'lead gen', 'lead type'],
      isVisible: hasNewLeadSystem
    }
  ),

  // Pages
  ...keyed(
    'pages',
    'SETTINGS.PAGES',
    ['request', 'reference', 'references_submitted'],
    { keywords: ['page', 'header'] }
  ),
  ...keyed('pages', 'SETTINGS.PAGES', ['feedback'], {
    keywords: ['page', 'header'],
    isVisible: hasAnyFeedback
  }),
  ...keyed('pages', 'SETTINGS.PAGES', ['feedback_employer'], {
    keywords: ['page', 'header'],
    isVisible: hasCandidateFeedback
  }),
  ...keyed('pages', 'SETTINGS.PAGES', ['pre_screening'], {
    keywords: ['page', 'header'],
    isVisible: hasPreScreening
  }),
  ...keyed('pages', 'SETTINGS.PAGES', ['candidate_authorization'], {
    keywords: ['page', 'consent'],
    isVisible: ({ settings }) => !!settings?.is_candidate_authorization_enabled
  }),
  ...keyed('pages', 'SETTINGS.PAGES', ['reference_authorization'], {
    keywords: ['page', 'consent'],
    isVisible: ({ settings }) =>
      !!settings?.settings_json?.reference_authorization
  }),

  // Emails
  ...templates(
    'emails',
    'SETTINGS.EMAILS',
    [
      { category: 'request', hasReminder: true },
      { category: 'reference', hasReminder: true },
      { category: 'permission' },
      { category: 'candidate_reference_completed' },
      {
        category: 'candidate_reference_pending',
        isVisible: hasCandidatePendingNotification
      },
      { category: 'recruiter_reference_completed' },
      { category: 'recruiter_all_references_completed' },
      { category: 'feedback', hasReminder: true, isVisible: hasAnyFeedback },
      {
        category: 'feedback_employer',
        hasReminder: true,
        isVisible: hasCandidateFeedback
      },
      {
        category: 'pre_screening',
        hasReminder: true,
        isVisible: hasPreScreening
      }
    ],
    ['email', 'template', 'subject']
  ),

  // Texts
  entry(
    'texts',
    'is_text_messages_enabled',
    'SETTINGS.TEXT_MESSAGES.IS_AVAILABLE.TITLE',
    'SETTINGS.TEXT_MESSAGES.IS_AVAILABLE.DESCRIPTION',
    {
      keywords: ['sms', 'text message']
    }
  ),
  ...templates(
    'texts',
    'SETTINGS.TEXTS',
    [
      { category: 'request', hasReminder: true },
      { category: 'reference', hasReminder: true },
      { category: 'feedback', hasReminder: true, isVisible: hasAnyFeedback },
      {
        category: 'feedback_employer',
        hasReminder: true,
        isVisible: hasCandidateFeedback
      },
      {
        category: 'pre_screening',
        hasReminder: true,
        isVisible: hasPreScreening
      }
    ],
    ['sms', 'text message', 'template'],
    hasTextMessages
  ),

  // Developers
  ...keyed(
    'developers',
    'SETTINGS.DEVELOPERS',
    ['is_webhook_enabled', 'webhook_url'],
    { keywords: ['api', 'integration'] }
  ),
  ...keyed(
    'developers',
    'SETTINGS.DEVELOPERS',
    ['is_dmarc_enabled', 'dmarc_domain'],
    { keywords: ['dmarc', 'dns', 'sendgrid', 'email security'] }
  ),

  // Data Retention
  ...keyed('data-retention', 'SETTINGS.DATA_RETENTION', ['is_enabled'], {
    keywords: ['anonymization', 'gdpr', 'data retention']
  }),
  ...keyed(
    'data-retention',
    'SETTINGS.DATA_RETENTION',
    ['retention_period_months'],
    {
      keywords: ['anonymization', 'gdpr', 'data retention'],
      isVisible: hasDataRetention
    }
  ),
  ...keyed('data-retention', 'SETTINGS.DATA_RETENTION', ['apply_to_children'], {
    keywords: ['anonymization', 'gdpr', 'data retention', 'sub-accounts'],
    isVisible: all(hasDataRetention, isParentAccount)
  }),
  entry(
    'data-retention',
    'status',
    'SETTINGS.DATA_RETENTION.status.TITLE',
    undefined,
    {
      keywords: ['anonymization', 'data retention', 'last run'],
      isVisible: hasDataRetention
    }
  ),

  // White Label
  entry(
    'white-label',
    'is_plugins_white_label_enabled',
    `${WHITE_LABEL}.TOGGLE.TITLE`,
    `${WHITE_LABEL}.TOGGLE.DESCRIPTION`,
    {
      keywords: ['branding']
    }
  ),
  entry(
    'white-label',
    'font_family',
    `${WHITE_LABEL}.FONT_FAMILY.TITLE`,
    `${WHITE_LABEL}.FONT_FAMILY.DESCRIPTION`,
    {
      keywords: ['branding', 'typography'],
      isVisible: hasWhiteLabel
    }
  ),
  entry(
    'white-label',
    'notification_email_from',
    `${WHITE_LABEL}.NOTIFICATION_SENDER.TITLE`,
    `${WHITE_LABEL}.NOTIFICATION_SENDER.DESCRIPTION`,
    {
      keywords: ['notification', 'sender', 'support email', 'team'],
      isVisible: hasWhiteLabel
    }
  ),
  entry(
    'white-label',
    'notification_email_name',
    `${WHITE_LABEL}.NOTIFICATION_SENDER.NAME`,
    undefined,
    {
      keywords: ['notification', 'sender', 'support email', 'team'],
      isVisible: hasWhiteLabel
    }
  ),
  ...keyed(
    'white-label',
    WHITE_LABEL,
    ['domain_name', 'explainer_video_link', 'user_guide_link'],
    {
      keywords: ['branding'],
      isVisible: hasWhiteLabel
    }
  ),

  // SSO
  entry('sso', 'connections', 'SETTINGS.SSO.CONNECTIONS', undefined, {
    keywords: ['sso', 'saml', 'single sign-on', 'okta', 'entra']
  }),
  entry('sso', 'scim', 'SETTINGS.SSO.SCIM', undefined, {
    keywords: ['sso', 'scim', 'provisioning']
  }),
  entry('sso', 'members', 'SETTINGS.SSO.MEMBERS', undefined, {
    keywords: ['sso', 'members', 'users']
  })
];
